import React, { useState, useEffect } from "react";
import "./App.css";

function App() {

  const [stories, setStories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [activeWordIndex, setActiveWordIndex] = useState(null);

  const [readStories, setReadStories] = useState(() => {
    const saved = localStorage.getItem("readStories");
    return saved ? JSON.parse(saved) : {};
  });

  // 🌐 FETCH STORIES + CATEGORIES
  useEffect(() => {

    Promise.all([

      fetch(
        "https://admin.infograph.plus/wp-json/wp/v2/posts?_embed&per_page=100"
      ).then((res) => res.json()),

      fetch(
        "https://admin.infograph.plus/wp-json/wp/v2/categories?per_page=100"
      ).then((res) => res.json())

    ])
      .then(([postsData, categoriesData]) => {

        const formattedStories = postsData.map((item) => ({

          id: item.id,

          title: item.title.rendered,

          content:
            item.content.rendered
              .replace(/<\/p>/g, "\n\n")
              .replace(/<br\s*\/?>/g, "\n")
              .replace(/<[^>]*>/g, "")
              .replace(/&nbsp;/g, " ")
              .trim(),

          categories: item.categories || [],

          image:
            item._embedded?.["wp:featuredmedia"]?.[0]
              ?.source_url ||
            "https://admin.infograph.plus/wp-content/uploads/2026/05/placeholder_sg.webp",

        }));

        const filteredCategories =
          categoriesData.filter(
            (cat) =>
              cat.count > 0 &&
              cat.name !== "Uncategorized"
          );

        setStories(formattedStories);

        setCategories(filteredCategories);

        setLoading(false);

      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });

  }, []);

  const speakWord = (word, uniqueId) => {

    const cleanWord = word.replace(/[.,!?]/g, "");

    const utterance =
      new SpeechSynthesisUtterance(cleanWord);

    utterance.lang = "en-US";
    utterance.rate = 0.7;

    utterance.onstart = () =>
      setActiveWordIndex(uniqueId);

    utterance.onend = () =>
      setActiveWordIndex(null);

    window.speechSynthesis.cancel();

    setTimeout(() => {
      window.speechSynthesis.speak(utterance);
    }, 50);

  };

  const toggleRead = (storyId) => {

    const updated = {
      ...readStories,
      [storyId]: !readStories[storyId],
    };

    setReadStories(updated);

    localStorage.setItem(
      "readStories",
      JSON.stringify(updated)
    );

  };

  const BackButton = () => (

    <button
      className="back-button"
      onClick={() => {

        setSelectedStory(null);

        setActiveWordIndex(null);

        window.speechSynthesis.cancel();

      }}
    >
      ⬅ Back to Stories
    </button>

  );

  // ⏳ LOADING
  if (loading) {

    return (
      <div className="container">
        <h1 className="title">
          🌱 Loading Story Garden...
        </h1>
      </div>
    );

  }

  // 🌟 CATEGORY SCREEN
  if (!selectedCategory) {

    return (

      <div className="container profile-screen home-theme">

        <h1 className="title">
          🌱 Story Garden
        </h1>

        <div className="profile-grid">

          {categories.map((category) => (

            <div
              key={category.id}
              className="profile-card"
              onClick={() =>
                setSelectedCategory(category)
              }
            >

              <div className="profile-emoji">
                📚
              </div>

              <h2>
                {category.name}
              </h2>

            </div>

          ))}

        </div>

      </div>

    );

  }

  // 📚 FILTER STORIES
  const filteredStories = stories.filter(
    (story) =>
      story.categories.includes(
        selectedCategory.id
      )
  );

  // 📚 STORY LIST
  if (!selectedStory) {

    return (

      <div
        className={`container ${
          selectedCategory?.name === "ATYAF"
            ? "atyaf-theme"
            : "mukarram-theme"
        }`}
      >

        <button
          className="back-button"
          onClick={() =>
            setSelectedCategory(null)
          }
        >
          ⬅ Back
        </button>

        <h1 className="title">
          🌱 {selectedCategory.name}
        </h1>

        <div className="grid">

          {filteredStories.map((story) => (

            <div
              key={story.id}
              className="card"
              onClick={() =>
                setSelectedStory(story)
              }
            >

              <img
                src={
                  story.image ||
                  "https://admin.infograph.plus/wp-content/uploads/2026/05/placeholder_sg.webp"
                }
                alt={story.title}
                className="story-image"
              />

              <h2>{story.title}</h2>

              {readStories[story.id] && (
                <div className="read-tick">
                  ✔
                </div>
              )}

            </div>

          ))}

        </div>

      </div>

    );

  }

  // 📖 STORY VIEW
  const paragraphs =
    selectedStory.content.split("\n\n");

  return (

    <div
      className={`container ${
        selectedCategory?.name === "ATYAF"
          ? "atyaf-theme"
          : "mukarram-theme"
      }`}
    >

      <BackButton />

      <h1 className="story-page-title">
        {selectedStory.title}
      </h1>

      <img
        src={
          selectedStory.image ||
          "https://admin.infograph.plus/wp-content/uploads/2026/05/placeholder_sg.webp"
        }
        alt={selectedStory.title}
        className="story-page-image"
      />

      {paragraphs.map((para, pIndex) => {

        const words =
          para.split(/(\s+)/);

        return (

          <p
            key={pIndex}
            className="story"
          >

            {words.map((word, wIndex) => {

              if (word.trim() === "")
                return word;

              const uniqueId =
                `${pIndex}-${wIndex}`;

              return (

                <span
                  key={uniqueId}
                  className={`word ${
                    activeWordIndex === uniqueId
                      ? "active"
                      : ""
                  }`}
                  onClick={(e) => {

                    e.stopPropagation();

                    speakWord(
                      word,
                      uniqueId
                    );

                  }}
                >
                  {word}
                </span>

              );

            })}

          </p>

        );

      })}

      <div className="read-circle-container">

        <button
          className={`read-circle ${
            readStories[selectedStory.id]
              ? "active"
              : ""
          }`}
          onClick={() =>
            toggleRead(selectedStory.id)
          }
        >
          ✔
        </button>

      </div>

    </div>

  );

}

export default App;